import { useWindowDimensions } from "@luce/core";

import { cn, platformProps } from "../../lib";
import {
  colorMap,
  getSpinnerArc,
  sizeMap,
  SPINNER_ROTATION_MS,
  spinnerDefaults,
  strokeMap,
  trailColor,
} from "./lib";
import type { SpinnerProps } from "./types";

export const Spinner = ({
  size = spinnerDefaults.size,
  color = spinnerDefaults.color,
  className,
  accessibilityLabel = spinnerDefaults.accessibilityLabel,
  decorative = spinnerDefaults.decorative,
  testID,
}: SpinnerProps) => {
  const { isDesktop } = useWindowDimensions();
  const sizeValue = sizeMap[size][isDesktop ? "desktop" : "mobile"];
  const strokeWidth = strokeMap[size][isDesktop ? "desktop" : "mobile"];
  const colorValue = colorMap[color];
  const { radius, circumference, arcLength } = getSpinnerArc(sizeValue, strokeWidth);

  return (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox={`0 0 ${sizeValue} ${sizeValue}`}
      // Still when reduced motion is requested, as on native
      className={cn("animate-spin motion-reduce:animate-none", className)}
      style={{ animationDuration: `${SPINNER_ROTATION_MS}ms` }}
      {...(decorative
        ? { "aria-hidden": true }
        : { role: "status", "aria-label": accessibilityLabel, "aria-busy": true })}
      {...platformProps({ testID, slot: "spinner" })}
    >
      {/* Trail circle (background) */}
      <circle
        cx={sizeValue / 2}
        cy={sizeValue / 2}
        r={radius}
        fill="none"
        stroke={trailColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />

      {/* Active spinner arc */}
      <circle
        cx={sizeValue / 2}
        cy={sizeValue / 2}
        r={radius}
        fill="none"
        stroke={colorValue}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${arcLength} ${circumference}`}
      />
    </svg>
  );
};

Spinner.displayName = "Spinner";
